import { InfoIcon } from "@chakra-ui/icons";
import {
  Box,
  Checkbox,
  CheckboxGroup,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { HasAccess } from "@intentsify/authorization/dist/react";
import { ForecastDTO, TempAccountFileDTO } from "@intentsify/dto";
import {
  DomainEnhancementStrategy,
  FirmographicsFilters,
  SegmentType,
  TargetPersona,
} from "@intentsify/types";
import { isPopulatedArray } from "@intentsify/utils";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Button,
  FilterAsyncTokenBased,
  LabeledSwitch,
  Tooltip,
  TreeFilter,
} from "components";
import { defaultFirmoFilters } from "components/FirmographicsNew/Firmographics.const";
import isEqual from "lodash/isEqual";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useComponentColors } from "theme";
import { FetchDataParamsWithToken } from "types";
import { useUserAgencyCompany } from "../../../../../../queries/companies/useUserAgencyCompany";
import { getTargetPersonas } from "../../../../../TargetPersonas/useTargetPersonas";
import {
  forecast,
  getCampaignCountries,
  RequestForecast,
  requestForecastSchema,
} from "../../DigitalForecasting.requests";

const personaDataRequest = async (
  params: FetchDataParamsWithToken<keyof TargetPersona> & { companyId?: number }
) => {
  const { targetPersonas } = await getTargetPersonas(params);
  return {
    ...targetPersonas,
    results: targetPersonas.map((persona) => ({
      value: persona.id,
      label: persona.name,
      meta: persona,
    })),
  };
};

type ForecastProps = {
  files: TempAccountFileDTO[];
  blocklistedFiles: TempAccountFileDTO[];
  firmographicsFilters: FirmographicsFilters;
  onReset: () => void;
};

const formDefaultValues = {
  name: "",
  includeISP: false,
  accountFiles: [],
  firmographicsFilters: undefined,
  countries: [],
  segments: [SegmentType.IP],
  targetPersonas: [],
  domainEnhancementStrategy: undefined,
};

const US_COUNTRY_ID = 234;

const Forecast = ({
  files,
  blocklistedFiles,
  firmographicsFilters,
  onReset,
}: ForecastProps) => {
  const componentColors = useComponentColors();

  const toast = useToast();
  const userAgencyCompany = useUserAgencyCompany();
  const selectedCompanyId = userAgencyCompany.data?.companyId;

  const { mutate: runForecast, isLoading } = useMutation(forecast, {
    onSuccess: () => {
      reset();
      onReset();

      toast({
        title:
          "Your Impression Forecasting request has been sent. You should receive an email with the results shortly.",
        status: "success",
      });
    },
  });

  const { data: regionsTree } = useQuery(
    ["digitalForecastingRegionsTree"],
    getCampaignCountries
  );

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
    setValue,
    getValues,
    watch,
    reset,
  } = useForm<RequestForecast>({
    mode: "onChange",
    resolver: zodResolver(requestForecastSchema),
    defaultValues: formDefaultValues,
  });

  const onSubmitHandler = handleSubmit(() => {
    const didNotEditFirmographics = isEqual(
      firmographicsFilters,
      defaultFirmoFilters
    );
    const didNotUploadFiles = !isPopulatedArray(files);

    const dto = {
      ...getValues(),
      firmographicsFilters: didNotEditFirmographics
        ? undefined
        : firmographicsFilters,

      accountsFiles: files,
      blocklistedFiles: blocklistedFiles,
    } satisfies ForecastDTO;

    if (didNotEditFirmographics && didNotUploadFiles) {
      toast({
        title:
          "You need to build an audience. Please complete the Step 1 in order to proceed.",
        status: "warning",
      });
      return;
    }

    runForecast(dto);
  });

  const countries = watch("countries");
  const shouldIncludeISPs = watch("includeISP");
  const segments = watch("segments");
  const targetPersonas = watch("targetPersonas");
  const domainEnhancementStrategy = watch("domainEnhancementStrategy");

  useEffect(() => {
    setValue("targetPersonas", []);
  }, [selectedCompanyId, setValue]);

  return (
    <>
      <Stack spacing={[1, 3, 5]} marginY={6}>
        <Controller
          name="name"
          control={control}
          render={({ fieldState: { error, invalid } }) => (
            <FormControl isRequired isInvalid={invalid}>
              <FormLabel
                fontSize="sm"
                fontWeight="md"
                color={componentColors.form.formLabelColor}
              >
                Enter forecasting name
              </FormLabel>

              <Input
                {...register("name")}
                type="text"
                placeholder="Name"
                shadow="sm"
                size="sm"
                rounded="md"
                autoComplete="off"
                isRequired
                w="400px"
              />
              <FormErrorMessage>{error?.message}</FormErrorMessage>
            </FormControl>
          )}
        />

        <Controller
          name="countries"
          control={control}
          render={({ field, fieldState: { error, invalid } }) => (
            <FormControl width={400} isRequired isInvalid={invalid}>
              <FormLabel
                fontSize="sm"
                fontWeight="md"
                color={componentColors.form.formLabelColor}
              >
                Countries
              </FormLabel>

              <TreeFilter
                subject="countries"
                nodes={regionsTree ?? []}
                included={countries}
                onIncludedChange={(selected) => {
                  field.onChange(
                    selected.map((country) => ({
                      label: country.label,
                      value: Number(country.value),
                    }))
                  );

                  /*
                    if USA was selected and removed we need to remove MAID segment and USER_ID segment type from segments and remove target personas
                  * */
                  const isUSASelected =
                    !!selected.find(
                      (country) => country.value === US_COUNTRY_ID
                    ) || false;
                  if (!isUSASelected) {
                    const filteredSegments =
                      segments.filter(
                        (segment) =>
                          ![SegmentType.MAID, SegmentType.USER_ID].includes(
                            segment
                          )
                      ) || [];
                    setValue("segments", filteredSegments);
                    setValue("targetPersonas", []);
                  }
                }}
              />
              <FormErrorMessage>{error?.message}</FormErrorMessage>
            </FormControl>
          )}
        />

        <FormControl isRequired>
          <FormLabel
            fontSize="sm"
            fontWeight="md"
            color={componentColors.form.formLabelColor}
          >
            Type
          </FormLabel>
          <CheckboxGroup colorScheme="teal" defaultValue={["ip"]}>
            <Stack spacing={[1, 5]} direction={["column", "row"]}>
              <Checkbox
                isChecked={segments.includes(SegmentType.IP)}
                onChange={(e) => {
                  setValue(
                    "segments",
                    e.target.checked
                      ? [...(segments ?? []), SegmentType.IP]
                      : segments?.filter(
                          (segment) => segment !== SegmentType.IP
                        ) ?? []
                  );
                }}
              >
                Account Targeting (IP)
              </Checkbox>
              <Checkbox
                isChecked={segments.includes(SegmentType.MAID)}
                onChange={(e) => {
                  setValue(
                    "segments",
                    e.target.checked
                      ? [...(segments ?? []), SegmentType.MAID]
                      : segments?.filter(
                          (segment) => segment !== SegmentType.MAID
                        ) ?? []
                  );
                }}
                isDisabled={
                  !countries.find((country) => country.value === US_COUNTRY_ID)
                }
              >
                Device Targeting (MAIDs)
              </Checkbox>
              <Checkbox
                isChecked={segments.includes(SegmentType.USER_ID)}
                onChange={(e) => {
                  setValue(
                    "segments",
                    e.target.checked
                      ? [...(segments ?? []), SegmentType.USER_ID]
                      : segments?.filter(
                          (segment) => segment !== SegmentType.USER_ID
                        ) ?? []
                  );
                }}
                isDisabled={
                  !countries.find((country) => country.value === US_COUNTRY_ID)
                }
              >
                Cookie Targeting (User IDs/Bitos)
              </Checkbox>
            </Stack>
          </CheckboxGroup>
        </FormControl>

        <FormControl>
          <FormLabel
            fontSize="sm"
            fontWeight="md"
            color={componentColors.form.formLabelColor}
          >
            Include ISPs
          </FormLabel>

          <LabeledSwitch
            leftLabel="No"
            rightLabel="Yes"
            isChecked={shouldIncludeISPs}
            defaultChecked={Boolean(shouldIncludeISPs)}
            onChange={(value) => {
              setValue("includeISP", value);
            }}
          />
        </FormControl>

        <FormControl>
          <FormLabel
            fontSize="sm"
            fontWeight="md"
            color={componentColors.form.formLabelColor}
          >
            Target personas
            <Tooltip
              aria-label="Target personas"
              label={`Select United States as one of the countries to target and the Device Targeting (MAIDs) type if you'd like to receive results around your target personas. Please note that your forecast will be significantly lower due to targeting only the personas within your accounts.`}
            >
              <InfoIcon
                color={componentColors.form.formLabelColor}
                w={4}
                h={4}
                ml={2}
              />
            </Tooltip>
          </FormLabel>
          <Box width="sm">
            <FilterAsyncTokenBased
              isMulti={true}
              isDisabled={
                !selectedCompanyId ||
                (!segments.includes(SegmentType.MAID) &&
                  !segments.includes(SegmentType.USER_ID))
              }
              defaultOptions={[]}
              currentValue={targetPersonas.map((item) => ({
                value: item.id,
                label: item.name,
              }))}
              dataRequest={(
                arg: FetchDataParamsWithToken<keyof TargetPersona>
              ) => {
                return personaDataRequest({
                  ...arg,
                });
              }}
              resetFilterRequestDependencies={[selectedCompanyId]}
              onFilterValuesChange={(options) => {
                setValue(
                  "targetPersonas",
                  (options || []).map((item) => ({
                    id: item.value,
                    name: item.label,
                  }))
                );
              }}
            />
          </Box>
        </FormControl>

        <HasAccess to="digitalForecasting.domainExpansion">
          <FormControl>
            <FormLabel
              fontSize="sm"
              fontWeight="md"
              color={componentColors.form.formLabelColor}
            >
              Domain Expansion
              <Tooltip
                aria-label={"expand"}
                label={
                  <>
                    Using Domain Expansion, our system will expand the TAL using
                    related parent and child domains.
                  </>
                }
              >
                <InfoIcon
                  color={componentColors.form.formLabelColor}
                  w={4}
                  h={4}
                  ml={2}
                />
              </Tooltip>
            </FormLabel>

            <Flex
              color="black"
              gap="1"
              alignItems="center"
              flexDir="row"
              _dark={{ color: "white" }}
            >
              <Checkbox
                id="expand"
                isChecked={
                  domainEnhancementStrategy === DomainEnhancementStrategy.expand
                }
                onChange={(e) => {
                  const domainEnhancementStrategy = e.target.checked
                    ? DomainEnhancementStrategy.expand
                    : undefined;

                  setValue(
                    "domainEnhancementStrategy",
                    domainEnhancementStrategy
                  );
                }}
              />

              <Text fontSize="sm">
                Domain Expansion: Expand my list using related domains
              </Text>
            </Flex>
          </FormControl>
        </HasAccess>
      </Stack>

      <Divider mt={4} />

      <Flex mt={4} justifyContent="flex-end">
        <Button
          size="md"
          variant="primary-teal"
          isLoading={isLoading}
          isDisabled={
            !isPopulatedArray(segments || !!Object.keys(errors).length)
          }
          onClick={() => void onSubmitHandler()}
        >
          Process Forecasting
        </Button>
      </Flex>
    </>
  );
};

export { Forecast };
